import type { ReactElement } from "react";
import {
  IconCamera,
  IconEye,
  IconHomePlus,
  IconMountain,
  IconPhoto,
  IconPlugConnected,
  IconRipple,
  IconRuler2,
  IconStack2,
  IconSun
} from "@tabler/icons-react";
import {
  defaultVisibility,
  type LayerVisibility,
  type ViewState
} from "../../state/layerState";
import type { PlacedStructure } from "../../state/structuresState";
import type { StructureEarthwork } from "../../state/computeStructureEarthwork";
import type { UtilityInference } from "@shared/api";
import type { SolarPotentialReport } from "../../score/computeSolarPotential";
import {
  BASE_VISIBILITY_KEYS,
  BaseLayersCard,
  type BaseVisibilityKey
} from "./BaseLayersCard";
import {
  TERRAIN_GENERAL_KEYS,
  TERRAIN_SLOPE_KEYS,
  TerrainLayersCard,
  type TerrainVisibilityKey
} from "./TerrainLayersCard";
import {
  SUN_VISIBILITY_KEYS,
  SunLayersCard,
  type SunPresetId,
  type SunVisibilityKey
} from "./SunLayersCard";
import {
  VIEWS_VISIBILITY_KEYS,
  ViewsLayersCard,
  type ViewsVisibilityKey
} from "./ViewsLayersCard";
import {
  FLOOD_VISIBILITY_KEYS,
  FloodLayersCard,
  type FloodVisibilityKey
} from "./FloodLayersCard";
import { UtilitiesLayersCard } from "./UtilitiesLayersCard";
import { StructuresCard, type StructureBoundsFt } from "./StructuresCard";
import { IllustrationsCard } from "./IllustrationsCard";
import {
  IconRail,
  RAIL_ICON_SIZE,
  RAIL_ICON_STROKE,
  RailPanelHeaderAction,
  type RailAction,
  type RailBadge,
  type RailModule
} from "../rail/IconRail";

export type InspectorRailsModuleId =
  | "illustrations"
  | "base"
  | "structures"
  | "terrain"
  | "sun"
  | "views"
  | "flood"
  | "utilities";

/** Ordered module specs for the left rail (icon + label). Shared with the handset strip. */
export const INSPECTOR_RAIL_MODULES: ReadonlyArray<{
  id: InspectorRailsModuleId;
  label: string;
  Icon: typeof IconStack2;
}> = [
  { id: "illustrations", label: "Renders", Icon: IconPhoto },
  { id: "base", label: "Base layers", Icon: IconStack2 },
  { id: "structures", label: "Structures", Icon: IconHomePlus },
  { id: "terrain", label: "Terrain", Icon: IconMountain },
  { id: "sun", label: "Sun", Icon: IconSun },
  { id: "views", label: "Views", Icon: IconEye },
  { id: "flood", label: "Flood & water", Icon: IconRipple },
  { id: "utilities", label: "Utilities", Icon: IconPlugConnected }
];

/** Visibility keys each module owns — drives the "something is on" rail dot. */
const MODULE_VISIBILITY_KEYS: Partial<
  Record<InspectorRailsModuleId, ReadonlyArray<keyof LayerVisibility>>
> = {
  base: BASE_VISIBILITY_KEYS,
  terrain: [...TERRAIN_GENERAL_KEYS, ...TERRAIN_SLOPE_KEYS],
  sun: SUN_VISIBILITY_KEYS,
  views: VIEWS_VISIBILITY_KEYS,
  flood: FLOOD_VISIBILITY_KEYS,
  utilities: ["utilitiesOverlay"]
};

const DEFAULT_VISIBILITY: LayerVisibility = defaultVisibility();

/**
 * Visibility keys of `id` whose current value differs from the viewer
 * default. Empty for a fresh viewer — nothing is badged until the user
 * changes something.
 */
export function inspectorModuleModifiedKeys(
  id: InspectorRailsModuleId,
  visibility: LayerVisibility
): ReadonlySet<string> {
  const keys = MODULE_VISIBILITY_KEYS[id];
  if (!keys) return new Set();
  return new Set(
    keys.filter((k) => visibility[k] !== DEFAULT_VISIBILITY[k]).map(String)
  );
}

/**
 * Badge for a module: count of placed structures, or a dot when any of the
 * module's layers has been changed from its default. The same keys get an
 * accent dot on their row inside the panel (see `InspectorRow.modified`).
 */
export function inspectorModuleBadge(
  id: InspectorRailsModuleId,
  viewState: ViewState
): RailBadge {
  if (id === "structures") {
    return viewState.structures.list.length || null;
  }
  return inspectorModuleModifiedKeys(id, viewState.visibility).size > 0
    ? "dot"
    : null;
}

/** Restore every visibility key owned by `id` to its default via the module's toggle handler. */
export function resetInspectorModuleToDefaults(
  id: InspectorRailsModuleId,
  props: Pick<
    InspectorRailsProps,
    | "viewState"
    | "onBaseVisibilityToggle"
    | "onTerrainVisibilityToggle"
    | "onSunVisibilityToggle"
    | "onViewsVisibilityToggle"
    | "onFloodVisibilityToggle"
    | "onUtilitiesOverlayToggle"
  >
): void {
  const modified = inspectorModuleModifiedKeys(id, props.viewState.visibility);
  for (const key of modified) {
    const value = DEFAULT_VISIBILITY[key as keyof LayerVisibility];
    switch (id) {
      case "base":
        props.onBaseVisibilityToggle(key as BaseVisibilityKey, value);
        break;
      case "terrain":
        props.onTerrainVisibilityToggle(key as TerrainVisibilityKey, value);
        break;
      case "sun":
        props.onSunVisibilityToggle(key as SunVisibilityKey, value);
        break;
      case "views":
        props.onViewsVisibilityToggle(key as ViewsVisibilityKey, value);
        break;
      case "flood":
        props.onFloodVisibilityToggle(key as FloodVisibilityKey, value);
        break;
      case "utilities":
        props.onUtilitiesOverlayToggle(value);
        break;
      default:
        break;
    }
  }
}

export function inspectorModuleIcon(id: InspectorRailsModuleId): ReactElement {
  const spec = INSPECTOR_RAIL_MODULES.find((m) => m.id === id)!;
  return <spec.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />;
}

export interface InspectorRailsProps {
  reportToken: string | null;
  illustrationsVersion: number;
  /** Single-open contextual panel; null = toolbar only (map fully visible). */
  activeModule: InspectorRailsModuleId | null;
  onSelectModule: (moduleId: InspectorRailsModuleId | null) => void;
  /** See {@link IconRailProps.externalOpenToken}. */
  externalOpenToken?: number;
  /** Scene measure tool toggle (toolbar slot). Omitted = no measure button. */
  measureActive?: boolean;
  onMeasureToggle?: () => void;
  onIllustrationsBegin: () => void;
  viewState: ViewState;
  onBaseVisibilityToggle: (key: BaseVisibilityKey, value: boolean) => void;
  /** Availability footnotes for base layers with no data (see BaseLayersCard). */
  baseLayerNotes?: Partial<Record<BaseVisibilityKey, string>>;
  onTerrainVisibilityToggle: (key: TerrainVisibilityKey, value: boolean) => void;
  onSunVisibilityToggle: (key: SunVisibilityKey, value: boolean) => void;
  onSunMonthDayHourChange: (
    month1: number,
    day: number,
    hourFloat: number,
    phase: "drag" | "commit"
  ) => void;
  onSunPreset: (preset: SunPresetId) => void;
  /** Terrain-adjusted PVWatts annual production for the Sun card's
   *  "Solar potential" row. Null until `/v1/solar` lands or when the
   *  worker has no NREL key. */
  solar: SolarPotentialReport | null;
  onViewsVisibilityToggle: (key: ViewsVisibilityKey, value: boolean) => void;
  onViewsEyeHeightLive: (ft: number) => void;
  onViewsEyeHeightRebuild: () => void;
  onViewsPickViewpoint: () => void;
  onViewsResetViewpoint: () => void;
  onFloodVisibilityToggle: (key: FloodVisibilityKey, value: boolean) => void;
  onFloodRefreshExternalData: () => void;
  utilityInferences: UtilityInference[] | null;
  onUtilitiesOverlayToggle: (value: boolean) => void;
  /** Bounds within which placed structures may move (parcel ring bbox, ft). */
  structureBoundsFt: StructureBoundsFt;
  /** False for read-only recipients — disables the Structures card. */
  structuresEditable: boolean;
  onStructuresBeginDraw: () => void;
  /** Run the auto-placer; owner code commits and bumps. */
  onStructuresAutoPlace: () => void;
  /** False when there's no qualifying flat region (auto-place greyed out). */
  canAutoPlace: boolean;
  /** Reasoning sentence for an auto-placed structure (null otherwise). */
  getAutoPlacementReasoning: (structureId: string) => string | null;
  onStructureUpdate: (id: string, partial: Partial<PlacedStructure>) => void;
  onStructureDelete: (id: string) => void;
  onStructureSectionToggle: (id: string) => void;
  /** Live cut/fill estimate for the inspector earthwork table. */
  getEarthworkForStructure: (
    structure: PlacedStructure
  ) => StructureEarthwork | null;
}

/**
 * Left-edge map controls: the icon rail that opens ONE layer/tool panel at
 * a time. Thin adapter over {@link IconRail}; the card bodies are shared
 * with the handset bottom sheet via {@link renderInspectorModuleBody}.
 */
export function InspectorRails(props: InspectorRailsProps): ReactElement {
  const {
    reportToken,
    activeModule,
    onSelectModule,
    measureActive,
    onMeasureToggle,
    viewState
  } = props;

  const modules: RailModule<InspectorRailsModuleId>[] = INSPECTOR_RAIL_MODULES.filter(
    (m) => m.id !== "illustrations" || reportToken != null
  ).map((m) => {
    const modified = inspectorModuleModifiedKeys(m.id, viewState.visibility);
    return {
      id: m.id,
      label: m.label,
      icon: <m.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />,
      badge: inspectorModuleBadge(m.id, viewState),
      headerTrailing:
        modified.size > 0 ? (
          <RailPanelHeaderAction
            label="Reset"
            title="Restore this panel's layers to their defaults"
            onClick={() => resetInspectorModuleToDefaults(m.id, props)}
          />
        ) : undefined,
      render: () => renderInspectorModuleBody(m.id, props, "inspector")
    };
  });

  const actions: RailAction[] = [];
  if (onMeasureToggle != null) {
    actions.push({
      id: "measure",
      label: "Measure distance",
      icon: <IconRuler2 size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />,
      active: measureActive === true,
      onClick: onMeasureToggle
    });
  }
  if (reportToken != null) {
    actions.push({
      id: "create-render",
      label: "Create render",
      icon: <IconCamera size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />,
      onClick: () => {
        props.onIllustrationsBegin();
      }
    });
  }

  return (
    <IconRail
      side="left"
      ariaLabel="Map tools"
      modules={modules}
      actions={actions}
      activeId={activeModule}
      onSelect={onSelectModule}
      externalOpenToken={props.externalOpenToken}
    />
  );
}

/**
 * Body for one left-rail module. Shared by the desktop flyout and the
 * handset bottom sheet (`presentation="drawer"`).
 */
export function renderInspectorModuleBody(
  moduleId: InspectorRailsModuleId,
  props: Omit<
    InspectorRailsProps,
    "activeModule" | "onSelectModule" | "externalOpenToken"
  >,
  presentation: "inspector" | "drawer"
): ReactElement | null {
  const { visibility, sun, views, structures } = props.viewState;
  const modifiedKeys = inspectorModuleModifiedKeys(moduleId, visibility);

  switch (moduleId) {
    case "illustrations":
      return (
        <IllustrationsCard
          reportToken={props.reportToken}
          structuresCount={structures.list.length}
          illustrationsVersion={props.illustrationsVersion}
          onBeginIllustration={props.onIllustrationsBegin}
          presentation={presentation}
        />
      );
    case "base":
      return (
        <BaseLayersCard
          visibility={visibility}
          modifiedKeys={modifiedKeys}
          layerNotes={props.baseLayerNotes}
          onToggle={props.onBaseVisibilityToggle}
          presentation={presentation}
        />
      );
    case "structures":
      return (
        <StructuresCard
          state={structures}
          bounds={props.structureBoundsFt}
          editable={props.structuresEditable}
          onBeginDraw={props.onStructuresBeginDraw}
          onAutoPlace={props.onStructuresAutoPlace}
          canAutoPlace={props.canAutoPlace}
          getAutoPlacementReasoning={props.getAutoPlacementReasoning}
          onStructureUpdate={props.onStructureUpdate}
          onStructureDelete={props.onStructureDelete}
          onSectionToggle={props.onStructureSectionToggle}
          getEarthworkForStructure={props.getEarthworkForStructure}
          presentation={presentation}
        />
      );
    case "terrain":
      return (
        <TerrainLayersCard
          visibility={visibility}
          modifiedKeys={modifiedKeys}
          onToggle={props.onTerrainVisibilityToggle}
          presentation={presentation}
        />
      );
    case "sun":
      return (
        <SunLayersCard
          visibility={visibility}
          modifiedKeys={modifiedKeys}
          dateTime={sun.dateTime}
          onToggle={props.onSunVisibilityToggle}
          onMonthDayHourChange={props.onSunMonthDayHourChange}
          onPreset={props.onSunPreset}
          solar={props.solar}
          presentation={presentation}
        />
      );
    case "views":
      return (
        <ViewsLayersCard
          visibility={visibility}
          modifiedKeys={modifiedKeys}
          eyeHeightFt={views.eyeHeightFt}
          onToggle={props.onViewsVisibilityToggle}
          onEyeHeightLive={props.onViewsEyeHeightLive}
          onEyeHeightRebuild={props.onViewsEyeHeightRebuild}
          onPickViewpoint={props.onViewsPickViewpoint}
          onResetViewpoint={props.onViewsResetViewpoint}
          presentation={presentation}
        />
      );
    case "flood":
      return (
        <FloodLayersCard
          visibility={visibility}
          modifiedKeys={modifiedKeys}
          onToggle={props.onFloodVisibilityToggle}
          onRefreshExternalData={props.onFloodRefreshExternalData}
          presentation={presentation}
        />
      );
    case "utilities":
      return (
        <UtilitiesLayersCard
          visibility={visibility}
          modifiedKeys={modifiedKeys}
          utilityInferences={props.utilityInferences}
          onUtilitiesOverlayToggle={props.onUtilitiesOverlayToggle}
          presentation={presentation}
        />
      );
    default:
      return null;
  }
}
